import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { checkSuspension } from '../services/suspensionCheck';
import { setupKeysAndDevice } from '../services/signingSetup';
import type { HydrationDeps } from '../types';

export function useAuthHydration(deps: HydrationDeps) {
  const hydrateUser = useCallback(async (userId: string, checkSusp = false) => {
    try {
      // 1. Suspension check (early exit)
      if (checkSusp) {
        const suspended = await checkSuspension(userId);
        if (suspended) {
          toast('Account Suspended', {
            description: 'Your account has been suspended. Please contact support for assistance.',
          });
          await supabase.auth.signOut();
          return;
        }
      }

      // 2. Parallel hydration: profile, credits, admin, keys
      const results = await Promise.allSettled([
        deps.fetchProfile(userId),
        deps.fetchCredits(userId),
        deps.fetchAdminStatus(userId),
        setupKeysAndDevice(userId).then(key => deps.setPrivateKey(key)),
      ]);

      // 3. Self-heal: if profile is missing, wait and retry once
      const profileResult = results[0];
      if (profileResult.status === 'fulfilled' && profileResult.value === null) {
        await new Promise(r => setTimeout(r, 2000));
        await deps.fetchProfile(userId);
      }

      // 4. Device trust evaluation
      await deps.evaluateDeviceTrust(userId);
    } catch (e) {
      logger.error('Auth hydration failed:', e);
    } finally {
      deps.setIsAuthReady(true);
    }
  }, [deps]);

  return { hydrateUser };
}
